/* eslint-disable @typescript-eslint/no-unsafe-enum-comparison */
import { Fragment } from "react/jsx-runtime";
import { BookingStatus, ClockTypes } from "@repo/database";
import { groupBy } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { compareAsc, format, isSameDay } from "date-fns";
import { useTranslation } from "react-i18next";

import BookingItem from "~/components/booking/booking-item";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "~/components/ui/accordion";
import { useBooking } from "~/hooks/use-booking";
import { useUser } from "~/hooks/use-user";
import { cn } from "~/lib/utils";
import { useTempBookingStore } from "~/stores/use-booking-store";

export const Route = createFileRoute("/_main/bookings/_layout/guest-planning")({
  component: GuestPlanning,
});

function GuestPlanning() {
  const { t } = useTranslation("common");
  const { user } = useUser();

  const { selectedDate } = useTempBookingStore();

  const { bookings } = useBooking();

  const dayBookings = bookings.filter(
    (booking) =>
      isSameDay(booking.startTime, selectedDate) && booking.groupSize > 0,
  );

  const activeBookings = dayBookings.filter(
    (booking) => booking.bookingStatus === BookingStatus.active,
  );

  const groupedActiveBookings = groupBy(
    dayBookings
      .filter((booking) => booking.bookingStatus === BookingStatus.active)
      .sort((a, b) => compareAsc(a.startTime, b.startTime)),
    "startTime",
  );

  const otherBookings = groupBy(
    dayBookings
      .filter((booking) => booking.bookingStatus !== BookingStatus.active)
      .sort((a, b) => compareAsc(a.startTime, b.startTime)),
    "bookingStatus",
  );

  if (dayBookings.length === 0) {
    return (
      <div className="text-center font-bold flex items-center justify-center h-full">
        {t("Booking.no_bookings_yet")}
      </div>
    );
  }

  return (
    <div className="small-scrollbar overflow-auto">
      <div className="bg-blue-500 p-2 flex flex-row gap-2 sticky top-0 z-[99]">
        <span className="font-medium">{t("Booking.Status.planned")}</span>
        <div className="flex flex-row gap-1 items-center text-sm">
          <span>
            {t("Booking.DayView.Accordion.guests_label", {
              total_guests: activeBookings.reduce(
                (acc, curr) => acc + curr.groupSize,
                0,
              ),
            })}
          </span>
          <span>/</span>
          <span>
            {t("Booking.DayView.Accordion.bookings_label", {
              total_bookings: activeBookings.length,
            })}
          </span>
        </div>
      </div>
      {Object.entries(groupedActiveBookings).map(([startTime, bookings]) => {
        const totalGuests = bookings.reduce(
          (totalGuests, booking) => totalGuests + booking.groupSize,
          0,
        );
        const totalBookings = bookings.length;
        return (
          <Fragment key={startTime}>
            <div className="top-10 sticky z-50 flex h-8 w-full items-center gap-2 bg-black px-4 text-white">
              <span className="text-lg font-semibold">
                {format(
                  startTime,
                  user.clock === ClockTypes.military ? "HH:mm" : "hh:aa a",
                )}
              </span>
              <div className="flex flex-row gap-1 text-sm">
                <span>
                  {t("Booking.DayView.Accordion.guests_label", {
                    total_guests: totalGuests,
                  })}
                </span>
                <span>/</span>
                <span>
                  {t("Booking.DayView.Accordion.bookings_label", {
                    total_bookings: totalBookings,
                  })}
                </span>
              </div>
            </div>

            <div className="flex flex-col">
              {bookings
                .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
                .map((booking) => (
                  <BookingItem
                    booking={booking}
                    key={booking.id}
                    showProvider
                    showStatus={booking.bookingStatus !== BookingStatus.pending}
                  />
                ))}
            </div>
          </Fragment>
        );
      })}

      <Accordion type="multiple" defaultValue={Object.keys(otherBookings)}>
        {Object.entries(otherBookings)
          .sort(([statusA], [statusB]) => {
            if (statusA === BookingStatus.checked_in) return -1; // checked_in comes first
            if (statusB === BookingStatus.checked_in) return 1;
            if (statusA === BookingStatus.checked_out) return -1; // checked_out comes second
            if (statusB === BookingStatus.checked_out) return 1;
            if (statusA === BookingStatus.canceled) return -1; // canceled comes third
            if (statusB === BookingStatus.canceled) return 1;
            return 0; // preserve original order for others
          })
          .map(([bookingStatus, bookings]) => {
            const perTime = groupBy(
              bookings.sort((a, b) => compareAsc(a.startTime, b.startTime)),
              "startTime",
            );

            const totalGuests = bookings.reduce(
              (totalGuests, booking) => totalGuests + booking.groupSize,
              0,
            );

            const totalBookings = bookings.length;

            const status = bookingStatus as BookingStatus;

            return (
              <AccordionItem key={bookingStatus} value={bookingStatus}>
                <AccordionTrigger
                  className={cn(
                    "p-2 z-10 sticky top-0",
                    status === BookingStatus.checked_out && "bg-purple-500",
                    status === BookingStatus.canceled && "bg-gray-500",
                    status === BookingStatus.no_show && "bg-red-800",
                    status === BookingStatus.checked_in && "bg-green-600",
                  )}
                >
                  <div className="flex flex-row gap-2 items-center">
                    <span>
                      {status === BookingStatus.canceled &&
                        t("Booking.Status.canceled")}
                      {status === BookingStatus.checked_out &&
                        t("BookingStatus.checked_out")}
                      {status === BookingStatus.checked_in &&
                        t("Booking.Status.checked_in")}
                      {status === BookingStatus.no_show &&
                        t("Booking.Status.no_show")}
                    </span>

                    <div className="flex flex-row gap-1 text-sm font-normal">
                      <span>
                        {t("Booking.DayView.Accordion.guests_label", {
                          total_guests: totalGuests,
                        })}
                      </span>
                      <span>/</span>
                      <span>
                        {t("Booking.DayView.Accordion.bookings_label", {
                          total_bookings: totalBookings,
                        })}
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="p-0">
                  {Object.entries(perTime).map(([startTime, bookings]) => {
                    const totalGuests = bookings.reduce(
                      (totalGuests, booking) => totalGuests + booking.groupSize,
                      0,
                    );
                    const totalBookings = bookings.length;
                    return (
                      <>
                        <div
                          key={startTime}
                          className="top-0 sticky z-50 flex h-8 w-full items-center gap-2 bg-black px-4 text-white"
                        >
                          <span className="text-lg font-semibold">
                            {format(
                              startTime,
                              user.clock === ClockTypes.military
                                ? "HH:mm"
                                : "hh:aa a",
                            )}
                          </span>
                          <div className="flex flex-row gap-1 text-sm">
                            <span>
                              {t("Booking.DayView.Accordion.guests_label", {
                                total_guests: totalGuests,
                              })}
                            </span>
                            <span>/</span>
                            <span>
                              {t("Booking.DayView.Accordion.bookings_label", {
                                total_bookings: totalBookings,
                              })}
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col">
                          {bookings
                            .sort(
                              (a, b) =>
                                a.createdAt.getTime() - b.createdAt.getTime(),
                            )
                            .map((booking) => (
                              <BookingItem
                                booking={booking}
                                key={booking.id}
                                showProvider
                                showStatus={
                                  booking.bookingStatus !==
                                  BookingStatus.pending
                                }
                              />
                            ))}
                        </div>
                      </>
                    );
                  })}
                </AccordionContent>
              </AccordionItem>
            );
          })}
      </Accordion>
    </div>
  );
}
